import { useCallback, useEffect, useRef, useState } from "react";

// Max recording length. Balances "useful memo" against the backend's
// 350 KB base64 cap and MongoDB's 16 MB per-doc limit (audio itself lives
// in a sibling collection, but keeping the file small still keeps mobile
// networks and Chrome's clipboard happy).
const MAX_RECORDING_MS = 15_000;

// Pick the first supported audio mime type that our backend also accepts.
// Server allowlist mirrors this list — keep them in sync when you change
// either.
const SUPPORTED_MIMES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg",
];

const pickMime = () => {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  for (const m of SUPPORTED_MIMES) {
    if (window.MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(fr.error || new Error("read-fail"));
    fr.readAsDataURL(blob);
  });

/**
 * Tiny MediaRecorder wrapper for JournalCard's voice-memo button.
 *
 * Exposes:
 *   isSupported : boolean — MediaRecorder + one of our accepted mimes
 *   isRecording : boolean
 *   elapsedMs   : number  — updates ~4× / sec while recording
 *   start()     : Promise<void> — requests mic, begins capture, auto-stops
 *                                 at MAX_RECORDING_MS
 *   stop()      : Promise<{ dataUrl, durationMs }> — flushes buffered chunks
 *                                                    and revokes the stream
 *   cancel()    : void — discards the current recording without emitting
 *
 * Silently no-ops on unsupported browsers (older iOS Safari). Callers should
 * check `isSupported` before showing the record button.
 */
export const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const autoStopRef = useRef(null);
  const tickRef = useRef(null);
  // Bridges the recorder's async `onstop` event back to our `stop()` caller.
  const stopResolverRef = useRef(null);
  const cancelledRef = useRef(false);
  const [supportedMime] = useState(() => pickMime());

  const cleanupStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  // Safety: unmount → tear down the stream so the mic light doesn't stay on.
  useEffect(() => () => cleanupStream(), [cleanupStream]);

  const start = useCallback(async () => {
    if (isRecording || !supportedMime && supportedMime !== "") return;
    chunksRef.current = [];
    cancelledRef.current = false;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    // Pass empty string to let the browser pick when none of our
    // shortlisted mimes work but MediaRecorder exists (very old Safari).
    const rec = new MediaRecorder(stream, supportedMime ? { mimeType: supportedMime } : undefined);
    recorderRef.current = rec;
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onstop = async () => {
      cleanupStream();
      const dur = Date.now() - startedAtRef.current;
      setIsRecording(false);
      setElapsedMs(0);
      if (cancelledRef.current) {
        stopResolverRef.current?.(null);
        stopResolverRef.current = null;
        return;
      }
      // Assemble the buffered chunks into one blob, encode as data URL.
      const blob = new Blob(chunksRef.current, {
        type: rec.mimeType || supportedMime || "audio/webm",
      });
      try {
        const dataUrl = await blobToDataUrl(blob);
        stopResolverRef.current?.({ dataUrl, durationMs: dur });
      } catch (err) {
        stopResolverRef.current?.(null);
      } finally {
        stopResolverRef.current = null;
      }
    };
    startedAtRef.current = Date.now();
    rec.start();
    setIsRecording(true);
    setElapsedMs(0);
    // Cheap timer so the button can render a live elapsed counter.
    tickRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 250);
    // Hard cap — even if the user forgets to tap stop.
    autoStopRef.current = setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    }, MAX_RECORDING_MS);
  }, [isRecording, supportedMime, cleanupStream]);

  const stop = useCallback(() => new Promise((resolve) => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") {
      resolve(null);
      return;
    }
    stopResolverRef.current = resolve;
    recorderRef.current.stop();
  }), []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    } else {
      cleanupStream();
      setIsRecording(false);
      setElapsedMs(0);
    }
  }, [cleanupStream]);

  return {
    isSupported: Boolean(supportedMime !== null),
    isRecording,
    elapsedMs,
    maxMs: MAX_RECORDING_MS,
    start,
    stop,
    cancel,
  };
};

export default useVoiceRecorder;
